import type { PlanStepVendor, VendorOffer, VendorOffers } from "@internal/consumer"
import { useQuery } from "@tanstack/react-query"
import React from "react"
import { useIntl } from "react-intl"
import { getContentfulClient } from "#/lib/contentful/contentfulClient"
import { getContentfulLocale } from "#/lib/contentful/utils/contentfulLocaleUtils"
import { MemberActions } from "#/services/memberActionTracking/memberActions"
import { useMemberActionTracking } from "#/services/memberActionTracking/useMemberActionTracking"
import type { TypeCollectiveItem, TypeCollectiveItemSkeleton } from "#/types/contentful"
import type ResponseError from "#/types/responseError"
import buildCarrotApiRetryPolicy from "#/utils/CarrotApiRetryPolicy"
import { reportErrorMessage } from "#/utils/ErrorReporting"

const client = getContentfulClient()

// The Carrot Collective entries that sell each at-home test ("Mira - Panorama lab test", "Legacy").
const VENDOR_ENTRY_IDS: Readonly<Record<PlanStepVendor, string>> = {
  Mira: "6kz1TTQeM0cu4NugGKZPGL",
  Legacy: "48qrdPmJX7TOcRaxerZfiq"
}
const VENDORS = Object.keys(VENDOR_ENTRY_IDS) as PlanStepVendor[]

type VendorEntry = TypeCollectiveItem<"WITHOUT_UNRESOLVABLE_LINKS">

// Every consumer home load makes this read, so it asks for the two entries and the fields the
// dialog and its claim tracking read, not the whole Collective with its linked content.
async function loadVendorEntries(locale: string): Promise<VendorEntry[]> {
  const { items } = await client.withoutUnresolvableLinks.getEntries<TypeCollectiveItemSkeleton>({
    content_type: "collectiveItem",
    "sys.id[in]": Object.values(VENDOR_ENTRY_IDS),
    select: ["fields.internalName", "fields.headline", "fields.ctaMethod", "fields.ctaUrl", "fields.offerCopy"],
    include: 0,
    locale: getContentfulLocale(locale)
  })
  return items
}

function useVendorEntries() {
  const { locale } = useIntl()
  return useQuery<VendorEntry[], ResponseError>(["GetVendorOfferEntries", locale], () => loadVendorEntries(locale), {
    refetchOnWindowFocus: false,
    retry: buildCarrotApiRetryPolicy(3)
  })
}

function vendorEntry(entries: readonly VendorEntry[], vendor: PlanStepVendor): VendorEntry | undefined {
  return entries.find((entry) => entry.sys.id === VENDOR_ENTRY_IDS[vendor])
}

// A `dynamic` CTA is a POST that `DynamicCtaButton` builds at click time, so its `ctaUrl` is not a
// page a link can open.
function vendorOffer(entry: VendorEntry | undefined): VendorOffer | undefined {
  if (entry === undefined || entry.fields.ctaMethod !== "static" || !entry.fields.ctaUrl) return undefined
  return { url: entry.fields.ctaUrl, offerCopy: entry.fields.offerCopy ?? null }
}

function vendorOffers(entries: readonly VendorEntry[]): Partial<Record<PlanStepVendor, VendorOffer>> {
  const offers: Partial<Record<PlanStepVendor, VendorOffer>> = {}
  for (const vendor of VENDORS) {
    const offer = vendorOffer(vendorEntry(entries, vendor))
    if (offer !== undefined) offers[vendor] = offer
  }
  return offers
}

/**
 * The Mira and Legacy offers `ConsumerCarrotPlanPage` hands its vendor dialog, read here because the
 * package cannot reach the host's Contentful client.
 */
export function useVendorOffers(): VendorOffers {
  const { data, isError } = useVendorEntries()
  const offers = React.useMemo(() => (data === undefined ? undefined : vendorOffers(data)), [data])

  // The dialog falls back to the discounts page without a word to the member, so a recreated or
  // re-pointed entry would otherwise go unnoticed.
  React.useEffect(() => {
    if (offers === undefined) return
    for (const vendor of VENDORS) {
      if (offers[vendor] === undefined) {
        reportErrorMessage(
          `No static Carrot Collective offer for ${vendor} (entry ${VENDOR_ENTRY_IDS[vendor]}); the consumer plan's vendor dialog falls back to the discounts page`
        )
      }
    }
  }, [offers])

  // Ahead of `isError`: a failed background refetch keeps the cached entries, and they still link.
  if (offers !== undefined) return { status: "loaded", offers }
  return isError ? { status: "failed" } : { status: "loading" }
}

/**
 * Tracks a Continue to a vendor's site as the claim the Collective tile's CTA tracks, so claims
 * count the same whichever surface the member leaves from. `useTrackCollectiveItemAction`'s payload
 * minus `memberJourney`: `useJourney` would put its plan, benefit and journey reads on every
 * consumer home load. `source` tells these claims apart from the tile's, which sends none.
 */
export function useTrackVendorClaim(): (vendor: PlanStepVendor) => void {
  const { data } = useVendorEntries()
  const { mutate: addMemberAction } = useMemberActionTracking()
  return (vendor) => {
    const fields = data === undefined ? undefined : vendorEntry(data, vendor)?.fields
    addMemberAction({
      memberActionTaken: MemberActions.CARROT_COLLECTIVE_CLICKED_CLAIM_DISCOUNT,
      metaData: {
        source: "carrot_plan",
        collectiveItem: { internalName: fields?.internalName, headline: fields?.headline, offerCopy: fields?.offerCopy }
      }
    })
  }
}
